import { useContext, useMemo } from 'react'

import {
  getIsPassepartoutSizeValid,
  getIsProfileValid,
  getIsPassepartoutColorEnabled,
} from './validation/state-is-valid'
import { FrameBuilderConfigurationContext } from '../contexts/configuration-context'
import { FrameBuilderSelectedStateContext } from '../contexts/state-context'

export interface EnabledItems {
  profileIds: string[]
  passepartoutSizeIds: string[]
  passepartoutColorIds: string[]
}

/**
 * Manages enabled frame builder items.
 */
export const useEnabledItems = (): EnabledItems => {
  const configuration = useContext(FrameBuilderConfigurationContext)
  const { selectedState } = useContext(FrameBuilderSelectedStateContext)

  // Enabled profiles
  const profileIds = useMemo(
    () =>
      configuration.profiles
        .filter((profile) =>
          getIsProfileValid(
            selectedState.artwork.width,
            selectedState.artwork.height,
            profile,
          ),
        )
        .map((profile) => profile._id),
    [
      configuration.profiles,
      selectedState.artwork.height,
      selectedState.artwork.width,
    ],
  )

  // Enabled passepartout sizes
  const passepartoutSizeIds = useMemo(
    () =>
      configuration.passepartout.sizes
        .filter((passepartoutSize) =>
          getIsPassepartoutSizeValid(
            selectedState.artwork.width,
            selectedState.artwork.height,
            passepartoutSize,
            selectedState.passepartout.color,
            selectedState.profile,
          ),
        )
        .map((passepartoutSize) => passepartoutSize._id),
    [
      configuration.passepartout.sizes,
      selectedState.artwork.height,
      selectedState.artwork.width,
      selectedState.passepartout.color,
      selectedState.profile,
    ],
  )

  // Enabled passepartout colors
  const passepartoutColorIds = useMemo(
    () =>
      configuration.passepartout.colors
        .filter((passepartoutColor) =>
          getIsPassepartoutColorEnabled(
            selectedState.passepartout.size,
            passepartoutColor,
            selectedState.profile,
          ),
        )
        .map((passepartoutColor) => passepartoutColor._id),
    [
      configuration.passepartout.colors,
      selectedState.passepartout.size,
      selectedState.profile,
    ],
  )

  return {
    profileIds,
    passepartoutSizeIds,
    passepartoutColorIds,
  }
}
